import { FormattedTime } from '@10xLabs/react-web/components/FormattedTime'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { ReactElement } from 'react'
import styled from 'styled-components'

import { DragIndicatorIcon } from 'components/Icons/DragIndicatorIcon'
import { StopIndex } from 'components/Map/StopIndex'
import { LocationDetails } from 'features/RoutingProjects/components/LocationDetails'
import {
  IDraggedLocation,
  ILocationRemove,
  IRoutingProjectPlanModel,
  IRoutingProjectPlanOptimization,
  IRoutingProjectPlanRouteStop
} from 'features/RoutingProjects/types'
import { rgbToHex } from 'utils/style'
import { CircleCloseIcon } from 'components/Icons'
import Tooltip from 'components/v2/Tooltip'
import { FormattedMessage } from 'react-intl'
import { isEmpty } from 'lodash'
import { usePlanContext } from 'features/RoutingProjects/containers/PlanContainer'

interface IRemoveLocationDisabledTooltipContentProps {
  routeHasMinStops: boolean
  locationIsDestinationConstraint: boolean
  locationIsOriginConstraint: boolean
}

const RemoveLocationDisabledTooltipContent = ({
  routeHasMinStops,
  locationIsDestinationConstraint,
  locationIsOriginConstraint
}: IRemoveLocationDisabledTooltipContentProps): ReactElement => {
  if (locationIsDestinationConstraint) {
    return (
      <FormattedMessage
        id="routingProjects.optimizedRoutesList.stopItem.removeLocationDisabled.routeHasMinStops"
        defaultMessage="This place cannot be deleted as it is set as a destination."
      />
    )
  } else if (locationIsOriginConstraint) {
    return (
      <FormattedMessage
        id="routingProjects.optimizedRoutesList.stopItem.removeLocationDisabled.locationIsDestinationConstraint"
        defaultMessage="This place cannot be deleted as it is set as an origin."
      />
    )
  } else if (routeHasMinStops) {
    return (
      <FormattedMessage
        id="routingProjects.optimizedRoutesList.stopItem.removeLocationDisabled.locationIsOriginConstraint"
        defaultMessage="This place cannot be deleted because a route requires at least two stops."
      />
    )
  }

  return (
    <FormattedMessage
      id="routingProjects.optimizedRoutesList.stopItem.removeLocationDisabled.default"
      defaultMessage="This place cannot be deleted."
    />
  )
}

const StopItem = styled.li<{ isDragging: boolean }>`
  display: grid;
  align-items: center;
  gap: 8px;
  grid-template-columns: min-content min-content 1fr min-content;
  opacity: ${({ isDragging }): number => (isDragging ? 0.5 : 1)};
`

const DragIndicatorContainer = styled.div`
  cursor: grab;
`

const Flex = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const StopItinerary = styled.div`
  font-size: 12px;
  color: #717171;
  font-weight: 400;
`

const CircleCloseIconContainer = styled.button`
  cursor: pointer;
  display: flex;
  padding: 0;
  border: none;
  background: none;

  :hover:not(:disabled) {
    opacity: 0.6;
  }

  :disabled {
    cursor: not-allowed;
  }
`

const StyledCircleCloseIcon = styled(CircleCloseIcon)`
  width: 20px;
  min-width: 20px;
  height: 20px;
  fill: #b0b0b0;
`

interface IRouteStopItemProps {
  stop: IRoutingProjectPlanRouteStop
  stopIndex: number
  rgbColor: string
  routeIndex: number
  draggedLocation: IDraggedLocation | null
  onLocationRemove?: (args: ILocationRemove) => void
  planModel: IRoutingProjectPlanModel
  currentOptimization: IRoutingProjectPlanOptimization
}

export const RouteStopItem = ({
  stop,
  stopIndex,
  rgbColor,
  routeIndex,
  draggedLocation,
  onLocationRemove,
  planModel,
  currentOptimization
}: IRouteStopItemProps): ReactElement => {
  const { handleLocationsCoordinatesUpdate } = usePlanContext()
  const route = currentOptimization.routes[routeIndex]
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: stop.sortableItemId,
    disabled: route.isLocked,
    data: {
      locationId: stop.location.id,
      locationIndex: stopIndex,
      routeIndex,
      location: stop.location,
      wasSkippedLocation: stop.location.wasSkipped
    }
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  }

  const shownLocationIndex =
    draggedLocation?.sortableItemId === stop.sortableItemId ? draggedLocation.locationIndex : stopIndex

  const handleCoordinatesUpdate = (coordinates: { latitude: number; longitude: number }): void => {
    handleLocationsCoordinatesUpdate({ locationIds: [stop.location.id], coordinates })
  }

  const handleLocationRemove = (): void => {
    onLocationRemove?.({ locationId: stop.location.id })
  }

  const locationIsOriginConstraint = stop.location.id === planModel.constraints.origin?.id
  const locationIsDestinationConstraint = stop.location.id === planModel.constraints.destination?.id
  const locationIsUsedByConstraints = locationIsOriginConstraint || locationIsDestinationConstraint
  const routeHasMinStops = currentOptimization.routes[routeIndex].stops.length <= 2
  const removeLocationIsDisabled = locationIsUsedByConstraints || routeHasMinStops
  const displayedTime = stop.departure?.time || stop.arrival?.time
  const isShowingItinerary = !isEmpty(displayedTime)

  return (
    <StopItem ref={setNodeRef} style={style} isDragging={isDragging}>
      <DragIndicatorContainer {...attributes} {...listeners}>
        {!route.isLocked && <DragIndicatorIcon />}
      </DragIndicatorContainer>
      <StopIndex color={rgbColor} bgColor={`${rgbToHex(rgbColor)}20`}>
        {shownLocationIndex + 1}
      </StopIndex>
      <LocationDetails location={stop.location} onCoordinatesUpdate={handleCoordinatesUpdate} isEditable />
      <Flex>
        {isShowingItinerary && (
          <StopItinerary>
            <FormattedTime time={displayedTime} noIcon />
          </StopItinerary>
        )}
        {!route.isLocked && (
          <Tooltip
            content={
              <RemoveLocationDisabledTooltipContent
                routeHasMinStops={routeHasMinStops}
                locationIsOriginConstraint={locationIsOriginConstraint}
                locationIsDestinationConstraint={locationIsDestinationConstraint}
              />
            }
            disabled={!removeLocationIsDisabled}
            position="right"
          >
            <CircleCloseIconContainer onClick={handleLocationRemove} disabled={removeLocationIsDisabled}>
              <StyledCircleCloseIcon />
            </CircleCloseIconContainer>
          </Tooltip>
        )}
      </Flex>
    </StopItem>
  )
}
